import useSearch from './store/useSearch';
import useTrips from './store/useTrips';
import useWhitelabelEnvs from './whitelabel/useWhitelabelEnvs';
import { filtersTypes } from '../constants/filtersTypes';

/**
 * Checks if a specific trip matches a specific recline option.
 * @param {Object} trip - The trip to check (plain object, not Immutable).
 * @param {string} selectedRecline - The recline value to check (e.g., "160").
 * @returns {boolean} Whether the trip matches the recline option or not.
 */
export const tripMatchesReclineOption = (trip, selectedRecline) => {
  const tripRecline = trip.seatsRecline;
  if (!tripRecline) return false;

  // Handle multiple reclinations separated by '/' (e.g., "160°/140°")
  const tripReclinations = tripRecline.split('/').map((r) => r.trim().replace('°', ''));

  // Check if the selected recline matches any of the trip's reclinations
  return tripReclinations.includes(selectedRecline);
};

const { RECLINE_OPTIONS } = filtersTypes;

/**
 * Hook to get the available recline options
 * @param {String} way - Way of the trips to filter
 * @returns Array
 */
const useAvailableReclineOptions = (way) => {
  const search = useSearch();
  const { trips } = useTrips();
  const environment = useWhitelabelEnvs();

  const searchId = search[way]?.id;
  const tripsList = trips[searchId]?.buses?.trips;
  const tripListFiltered = trips[searchId]?.buses?.trips?.filter((t) => !t.openTicket) || [];

  if (!tripsList || !tripListFiltered.length) {
    return [];
  }

  // Get available reclinations from environment
  const { availableReclinations = [] } = environment || {};

  // If no recline options are available from environment, return empty array
  if (!availableReclinations || availableReclinations.length === 0) {
    return [];
  }

  // Define the recline options based on the availableReclinations from environment
  const reclineOptions = availableReclinations.map((reclination) => ({
    id: `reclining_spot_${reclination}`,
    label: `common:amenity_reclining_spot_${reclination}`,
    title: `${reclination}°`,
    icon: 'Recline',
    iconColor: 'primary',
    value: { seatsRecline: reclination.toString() },
    filterType: RECLINE_OPTIONS,
  }));

  // Filter recline options to only include those that have actual trip availability
  const reclineOptionsWithAvailability = reclineOptions
    .map((option) => {
      // Check if there are actually trips with this recline option
      const hasMatchingTrips = tripListFiltered.some(
        (trip) => tripMatchesReclineOption(trip, option.value.seatsRecline),
        // eslint-disable-next-line function-paren-newline
      );

      return {
        ...option,
        hasAvailability: hasMatchingTrips,
      };
    })
    .filter((option) => option.hasAvailability); // Only return options with actual availability

  return reclineOptionsWithAvailability;
};

export default useAvailableReclineOptions;
